import React, { useState } from 'react';
import { Download, FileText, Share, Mail, Printer, Brain, Smartphone, Upload } from 'lucide-react';
import ComingSoonBanner from './common/ComingSoonBanner';
import { exportStoredData, importStoredData } from '../utils/dataTransfer';

export const DataExport: React.FC = () => {
  const [exportType, setExportType] = useState<'pdf' | 'csv' | 'json'>('pdf');
  const [dateRange, setDateRange] = useState('last-30-days');
  const [selectedData, setSelectedData] = useState({
    conditions: true,
    symptoms: true,
    medications: true,
    appointments: true,
    vitals: true,
    foodTracking: true,
    aiInsights: true,
  });

  const handleSimpleExport = (format: 'json' | 'csv') => {
    const data = exportStoredData(format);
    const blob = new Blob([data], {
      type: format === 'json' ? 'application/json' : 'text/csv',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `zippyhealth-data.${format}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const text = await file.text();
    const fmt = file.name.endsWith('.csv') ? 'csv' : 'json';
    const result = importStoredData(text, fmt);
    if (!result.success) {
      alert('Import errors:\n' + result.errors.join('\n'));
    } else {
      alert('Import completed successfully');
    }
  };

  const handleExport = () => {
    // Export logic would go here
    console.log('Exporting ZippyHealth data...', { exportType, dateRange, selectedData });
  };

  return (
    <div className="space-y-6">
      {/* Coming Soon Banner */}
      <ComingSoonBanner 
        featureName="ZippyHealth Data Export"
        description="Your data export system will generate real medical reports, connect with EHR systems, provide HIPAA-compliant sharing, and create AI-powered health summaries for healthcare providers."
        currentPage="/export"
        variant="compact"
        expectedRelease="very soon"
      />
      
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <img 
              src="/zippy-logo-simple.svg" 
              alt="ZippyHealth Logo"
              className="w-8 h-8"
            />
            <h2 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
              ZippyHealth Data Export
            </h2>
          </div>
          <p className="text-slate-600 mt-1">Export your comprehensive health data to share with healthcare providers</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => handleSimpleExport('json')}
              className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg flex items-center space-x-1"
            >
              <Download className="h-4 w-4" />
              <span>Export JSON</span>
            </button>
            <button
              onClick={() => handleSimpleExport('csv')}
              className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg flex items-center space-x-1"
            >
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
            <label className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg flex items-center space-x-1 cursor-pointer">
              <Upload className="h-4 w-4" />
              <span>Import</span>
              <input
                type="file"
                accept=".json,.csv"
                onChange={(e) => handleImportFile(e.target.files)}
                className="hidden"
              />
            </label>
          </div>
        </div>
      </div>

      {/* ZippyHealth Export Features */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Brain className="h-6 w-6 text-red-600" />
          <h3 className="text-lg font-semibold text-slate-800">ZippyHealth Smart Export Features</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <Smartphone className="h-6 w-6 text-blue-500 mb-2" />
            <h4 className="font-medium text-slate-800">AI-Curated Reports</h4>
            <p className="text-sm text-slate-600">Automatically highlight key insights and patterns</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <FileText className="h-6 w-6 text-green-500 mb-2" />
            <h4 className="font-medium text-slate-800">Doctor-Ready Format</h4>
            <p className="text-sm text-slate-600">Professional medical reports with talking points</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <Share className="h-6 w-6 text-purple-500 mb-2" />
            <h4 className="font-medium text-slate-800">Secure Sharing</h4>
            <p className="text-sm text-slate-600">HIPAA-compliant data sharing with encryption</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Export Configuration */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-6">Export Configuration</h3>
            
            {/* Export Format */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-3">Export Format</label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'pdf', label: 'PDF Report', icon: FileText, desc: 'Doctor-ready' },
                  { value: 'csv', label: 'CSV Data', icon: Download, desc: 'Raw data' },
                  { value: 'json', label: 'JSON Data', icon: FileText, desc: 'Full export' },
                ].map((format) => {
                  const Icon = format.icon;
                  return (
                    <button
                      key={format.value}
                      onClick={() => setExportType(format.value as 'pdf' | 'csv' | 'json')}
                      className={`p-4 border rounded-lg flex flex-col items-center space-y-2 transition-all ${
                        exportType === format.value
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="h-6 w-6" />
                      <span className="text-sm font-medium">{format.label}</span>
                      <span className="text-xs text-slate-500">{format.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date Range */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-3">Date Range</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              >
                <option value="last-7-days">Last 7 days</option>
                <option value="last-30-days">Last 30 days</option>
                <option value="last-90-days">Last 90 days</option>
                <option value="last-6-months">Last 6 months</option>
                <option value="last-year">Last year</option>
                <option value="all-time">All time</option>
                <option value="custom">Custom range</option>
              </select>
            </div>

            {/* Data Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-3">ZippyHealth Data to Include</label>
              <div className="space-y-3">
                {Object.entries(selectedData).map(([key, value]) => (
                  <label key={key} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => setSelectedData(prev => ({ ...prev, [key]: e.target.checked }))}
                      className="w-4 h-4 text-red-600 border-slate-300 rounded focus:ring-red-500"
                    />
                    <span className="text-sm text-slate-700 capitalize">
                      {key === 'vitals' ? 'Vital Signs' : 
                       key === 'foodTracking' ? 'Zippy Food Tracker Data' :
                       key === 'aiInsights' ? 'ZippyHealth AI Insights' : 
                       key}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Export Button */}
            <button
              onClick={handleExport}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-3 px-4 rounded-lg flex items-center justify-center space-x-2 transition-all transform hover:scale-105"
            >
              <Download className="h-5 w-5" />
              <span>Export ZippyHealth Data</span>
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full flex items-center space-x-3 p-3 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                <Mail className="h-5 w-5 text-blue-500" />
                <span className="text-sm font-medium">Email to Doctor</span>
              </button>
              <button className="w-full flex items-center space-x-3 p-3 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                <Printer className="h-5 w-5 text-green-500" />
                <span className="text-sm font-medium">Print Report</span>
              </button>
              <button className="w-full flex items-center space-x-3 p-3 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                <Share className="h-5 w-5 text-purple-500" />
                <span className="text-sm font-medium">Secure Share</span>
              </button>
              <button className="w-full flex items-center space-x-3 p-3 text-left border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
                <Brain className="h-5 w-5 text-red-500" />
                <span className="text-sm font-medium">ZippyHealth AI Summary</span>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Export History</h3>
            <div className="space-y-3">
              <div className="p-3 border border-slate-200 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-800">ZippyHealth Report</span>
                  <span className="text-xs text-slate-500">2 days ago</span>
                </div>
                <p className="text-xs text-slate-600">PDF • 30 days data • AI insights included</p>
              </div>
              <div className="p-3 border border-slate-200 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-800">Food Tracking Data</span>
                  <span className="text-xs text-slate-500">1 week ago</span>
                </div>
                <p className="text-xs text-slate-600">CSV • 90 days Zippy Food Tracker data</p>
              </div>
              <div className="p-3 border border-slate-200 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-800">Complete ZippyHealth Export</span>
                  <span className="text-xs text-slate-500">2 weeks ago</span>
                </div>
                <p className="text-xs text-slate-600">JSON • All time comprehensive data</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
